import { useRecruitmentApi } from 'infrastructure/api';
import type { ListResponse } from 'framework/list';
import type { Vacancy } from './types';

type VacancyGroupReference = {
  vacancyGroupId: string;
  vacancyGroupName: string | null;
};

export type VacancyGroupReferencesByVacancyId = Dictionary<VacancyGroupReference>;

type VacancyReference = Pick<Vacancy, 'id' | 'specialization' | 'vacancyGroupId'>;

const useVacancyReferenceService = () => {
  const { sendPostRequest } = useRecruitmentApi();

  const findVacancyGroupReferences = async (
    vacancyIds: Array<string | null | undefined>,
  ): Promise<VacancyGroupReferencesByVacancyId> => {
    const uniqueVacancyIds = Array.from(new Set(vacancyIds.filter((vacancyId): vacancyId is string => !!vacancyId)));

    if (uniqueVacancyIds.length === 0) {
      return {};
    }

    try {
      const response = await sendPostRequest('/vacancies/search', {
        filter: {
          id: {
            in: uniqueVacancyIds,
          },
        },
        pageSize: uniqueVacancyIds.length,
      });
      const { result = [] } = (await response.json()) as ListResponse<VacancyReference>;

      return Object.fromEntries(
        result
          .filter(vacancy => !!vacancy.vacancyGroupId)
          .map(vacancy => [
            vacancy.id,
            {
              vacancyGroupId: vacancy.vacancyGroupId,
              vacancyGroupName: vacancy.specialization || null,
            },
          ]),
      );
    } catch {
      return {};
    }
  };

  return {
    findVacancyGroupReferences,
  };
};

export default useVacancyReferenceService;
